import {KpiUpdate, ExpertSystemEquipmentType, useKpiSearch} from '@domain/expert-system'
import {areaDepartmentCodes} from '@hconnect/common/consts'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useKpiNodeIds} from '@hconnect/common/hooks'
import {InstallationStatus, KpiOption, User} from '@hconnect/common/types'
import {getFormPrimaryButtonSx} from '@hconnect/common/utils'
import {LoadingButton} from '@hconnect/uikit'
import {Close} from '@mui/icons-material'
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from '@mui/material'
import {AxiosError} from 'axios'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {AssignedUser, HierarchyTableNode, Item} from '../../types'
import {getKpisFromTableNode} from '../../utils'
import {FieldErrors, useResponsibleUsersValidation} from '../../validation'

import {KpiSection} from './KpiSection'
import {ResponsiblePersons} from './ResponsiblePersons'

type ResponsiblePersonDialogProps = {
  open: boolean
  equipment: ExpertSystemEquipmentType
  title: string
  users: User[]
  onClose: () => void
  onSave: (payload: KpiUpdate) => void
  isLoading: boolean
  selectedItem: HierarchyTableNode
}

export const ResponsiblePersonDialog: React.FC<ResponsiblePersonDialogProps> = ({
  open,
  title,
  equipment,
  users,
  onClose,
  onSave,
  isLoading,
  selectedItem
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {data: kpiResponse, isLoading: loadingKpis, error, isError} = useKpiSearch(selectedItem.id)
  const {validateEntries, isDirty, setIsDirty} = useResponsibleUsersValidation()
  useKpiNodeIds(selectedItem.id, areaDepartmentCodes)

  const kpiOptions = useMemo<KpiOption[]>(() => {
    if (!kpiResponse) return []
    return kpiResponse.processStages
      .flatMap((stage) => stage.definitionsWithResults)
      .map((definition) => ({
        label: definition.definition.displayName,
        id: String(definition.definition.kpiAssignment.id)
      }))
  }, [kpiResponse])

  const kpiOptionsMap = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>()
    kpiOptions.forEach((option) => {
      map.set(option.id, option.label)
    })
    return map
  }, [kpiOptions])

  const initialUsers = useMemo<AssignedUser[]>(
    () =>
      users.map((user) => ({
        userId: user.id,
        id: crypto.randomUUID(),
        name: user.name
      })),
    [users]
  )

  const getInitialKpis = useCallback(() => {
    const nodes = getKpisFromTableNode(selectedItem)
    return nodes.map((node) => {
      return {
        id: node.id,
        name: node.name,
        millType: node.millType,
        expertSystemName: node.expertSystemName,
        kpiAssignmentId: node.kpiAssignmentId ? String(node.kpiAssignmentId) : '',
        label: kpiOptionsMap.get(String(node.kpiAssignmentId || '')),
        provider: node.provider?.toLowerCase() || '',
        upmDepartmentId: node.upmDepartmentId || '',
        installationStatus: node.installationStatus || undefined,
        installationStatusText: node.installationStatusText || '',
        installationYear: node.installationYear ? String(node.installationYear) : ''
      }
    })
  }, [kpiOptionsMap, selectedItem])

  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>(initialUsers)
  const [kpiItems, setKpiItems] = useState<Item[]>([])
  const [errors, setErrors] = useState<FieldErrors>({})

  useEffect(() => {
    setKpiItems(getInitialKpis())
  }, [getInitialKpis])

  const handleSave = () => {
    const payload: KpiUpdate = {
      responsibleUserIds: assignedUsers.map((a) => a.userId),
      kpiNodes: kpiItems.map((item) => ({
        ...item,
        kpiAssignmentId: !item.kpiAssignmentId ? undefined : Number(item.kpiAssignmentId),
        provider: !item.provider ? undefined : item.provider,
        upmDepartmentId: !item.upmDepartmentId ? undefined : item.upmDepartmentId,
        installationStatus: !item.installationStatus ? undefined : item.installationStatus,
        installationStatusText:
          item.installationStatus === InstallationStatus.FreeText
            ? item.installationStatusText
            : undefined,
        installationYear: !item.installationYear ? undefined : Number(item.installationYear)
      }))
    }

    const validation = validateEntries(payload, assignedUsers, kpiItems)

    if (!validation.success) {
      return setErrors(validation.errors)
    }

    onSave(payload)
    setErrors({})
  }

  const disableError = !!error && error instanceof AxiosError && error.response?.status === 404

  return (
    <Dialog
      data-test-id="expert-system-responsible-users-dialog"
      open={open}
      fullWidth
      maxWidth="md"
      disablePortal
    >
      <DataContentWrapper
        data={kpiOptions}
        showLoader={loadingKpis}
        isError={disableError ? false : isError}
        isNoData={() => (disableError ? false : kpiOptions.length === 0)}
        progressSx={{color: 'primary.main'}}
      >
        <DialogTitle>
          <Typography sx={{fontWeight: '500'}} variant="h3" color="black">
            {title}
          </Typography>
          <Typography variant="subtitle2" color="GrayText">
            {t(`label.${equipment}s`)}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <ResponsiblePersons
            assignedUsers={assignedUsers}
            setAssignedUsers={setAssignedUsers}
            errors={errors}
            setIsDirty={setIsDirty}
          />
          <KpiSection
            kpiOptions={kpiOptions}
            items={kpiItems}
            setItems={setKpiItems}
            equipment={equipment}
            errors={errors}
            setIsDirty={setIsDirty}
            plantId={selectedItem.id}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            color="secondary"
            startIcon={<Close />}
            data-test-id="responsible-persons-cancel"
            disabled={isLoading}
          >
            {t('responsiblePersonDialog.action.cancel')}
          </Button>
          <LoadingButton
            onClick={handleSave}
            color="primary"
            variant="contained"
            sx={getFormPrimaryButtonSx}
            data-test-id="save-responsible-persons"
            disabled={isLoading || !isDirty}
            isLoading={isLoading}
          >
            {t('responsiblePersonDialog.action.save')}
          </LoadingButton>
        </DialogActions>
      </DataContentWrapper>
    </Dialog>
  )
}
